import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getSupabase } from "@/lib/database";
import { CategoryService } from "@/lib/domains/category";
import type { CategoryViewModel } from "@/lib/domains";
import { useCategoriesQuery, useInvalidateCategories } from "@/hooks/useCategoriesQuery";

export default function CategoriesSettingsPage() {
    const navigate = useNavigate();
    const [userId, setUserId] = useState<string | null>(null);
    const { data: categories = [], isLoading: categoriesLoading } = useCategoriesQuery();
    const invalidateCategories = useInvalidateCategories();
    const [stats, setStats] = useState<Array<{ category: CategoryViewModel; count: number; total: number }>>([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editForm, setEditForm] = useState<{ customName: string }>({
        customName: '',
    });

    useEffect(() => {
        async function init() {
            const supabase = getSupabase();
            const { data: { user } } = await supabase.auth.getUser();

            if (!user) {
                navigate('/auth');
                return;
            }

            setUserId(user.id);
            await loadStats(user.id);
        }

        init();
    }, [navigate]);

    // Update loading state based on React Query status
    useEffect(() => {
        if (!categoriesLoading) {
            setLoading(false);
        }
    }, [categoriesLoading]);

    async function loadStats(uid: string) {
        try {
            const statsData = await CategoryService.getCategoryStats(uid, 'light');
            setStats(statsData);
        } catch (error) {
            console.error('Failed to load stats:', error);
        }
    }

    async function handleToggleHidden(categoryId: string, isCurrentlyHidden: boolean) {
        if (!userId) return;

        try {
            if (isCurrentlyHidden) {
                await CategoryService.showCategory(categoryId, userId);
            } else {
                await CategoryService.hideCategory(categoryId, userId);
            }
            // Invalidate cache to trigger refetch
            invalidateCategories();
        } catch (error) {
            console.error('Failed to toggle category:', error);
            alert('Failed to update category');
        }
    }

    function startEdit(category: CategoryViewModel) {
        setEditingId(category.id);
        setEditForm({
            customName: category.customName || '',
        });
    }

    function cancelEdit() {
        setEditingId(null);
        setEditForm({ customName: '' });
    }

    async function saveEdit(categoryId: string) {
        if (!userId) return;

        // Validate name is not empty
        if (!editForm.customName.trim()) {
            alert('Category name cannot be empty');
            return;
        }

        try {
            await CategoryService.updateCategory(categoryId, userId, {
                customName: editForm.customName.trim(),
            });
            // Invalidate cache to trigger refetch
            invalidateCategories();
            cancelEdit();
        } catch (error) {
            console.error('Failed to update category:', error);
            alert('Failed to update category');
        }
    }

    const getUsageCount = (categoryId: string) => {
        const stat = stats.find(s => s.category.id === categoryId);
        return stat?.count || 0;
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-gray-600">Loading categories...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 pb-20">
            {/* Header */}
            <div className="bg-white border-b sticky top-0 z-10">
                <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-4">
                    <button
                        onClick={() => navigate(-1)}
                        className="text-gray-600 hover:text-gray-900"
                    >
                        ← Back
                    </button>
                    <h1 className="text-xl font-bold">Manage Categories</h1>
                </div>
            </div>

            <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
                {/* Info Box */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-900 mb-2">
                        <strong>Category System:</strong> Icons and colors are predefined for each category type.
                        You can customize the display name or hide categories you don&apos;t use.
                    </p>
                    <p className="text-xs text-blue-800">
                        💡 Expense counts are for reference only - they help you decide which categories to keep visible.
                    </p>
                </div>

                {/* All Categories */}
                <section>
                    <h2 className="text-lg font-semibold mb-4">
                        Your Categories ({categories.filter(c => !c.isHidden).length}/{categories.length} visible)
                    </h2>
                    <div className="bg-white rounded-lg border divide-y">
                        {categories.map(category => (
                            <div key={category.id} className="p-4">
                                {editingId === category.id ? (
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-3">
                                            <span
                                                className="text-3xl"
                                                style={{ color: category.color }}
                                            >
                                                {category.icon}
                                            </span>
                                            <div className="flex-1">
                                                <label className="block text-xs text-gray-600 mb-1">
                                                    Custom Name (leave empty for default: &quot;{category.displayName}&quot;)
                                                </label>
                                                <input
                                                    type="text"
                                                    value={editForm.customName}
                                                    onChange={(e) => setEditForm({ customName: e.target.value })}
                                                    className="w-full px-3 py-2 border rounded-lg"
                                                    placeholder={category.displayName}
                                                />
                                            </div>
                                        </div>
                                        <p className="text-xs text-gray-500">
                                            Category type: <strong>{category.categoryType}</strong> (icons and colors are fixed per type)
                                        </p>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => saveEdit(category.id)}
                                                className="flex-1 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                                            >
                                                Save
                                            </button>
                                            <button
                                                onClick={cancelEdit}
                                                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-4">
                                        <span
                                            className="text-3xl"
                                            style={{ color: category.color }}
                                        >
                                            {category.icon}
                                        </span>
                                        <div className="flex-1">
                                            <div className="font-medium">
                                                {category.displayName}
                                                {category.customName && (
                                                    <span className="ml-2 text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded">
                                                        customized
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {category.categoryType} • {getUsageCount(category.id)} expenses
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => handleToggleHidden(category.id, category.isHidden)}
                                                className={`px-3 py-1 rounded-lg text-sm ${category.isHidden
                                                    ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                                                    }`}
                                            >
                                                {category.isHidden ? 'Show' : 'Hide'}
                                            </button>
                                            <button
                                                onClick={() => startEdit(category)}
                                                className="px-3 py-1 bg-gray-100 rounded-lg text-sm hover:bg-gray-200"
                                            >
                                                Rename
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                    <p className="mt-2 text-sm text-gray-600">
                        💡 Tip: Hidden categories won&apos;t appear when adding expenses
                    </p>
                </section>

                {/* Usage Statistics */}
                {stats.length > 0 && (
                    <section>
                        <h2 className="text-lg font-semibold mb-4">Top 5 Categories by Usage</h2>
                        <div className="bg-white rounded-lg border p-4">
                            <div className="space-y-2">
                                {stats
                                    .sort((a, b) => b.count - a.count)
                                    .slice(0, 5)
                                    .map(stat => (
                                        <div key={stat.category.id} className="flex justify-between items-center">
                                            <div className="flex items-center gap-2">
                                                <span style={{ color: stat.category.color }}>
                                                    {stat.category.icon}
                                                </span>
                                                <span className="text-gray-700">{stat.category.displayName}</span>
                                            </div>
                                            <span className="font-semibold text-gray-900">{stat.count} expenses</span>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
}
